import Cookies from "js-cookie";

/**
 * Single source of truth for auth tokens and session-timing state.
 *
 * SHARED FILE — byte-identical copies live in prx_shell-ui, prx_buyerapp-ui,
 * prx_buyer_WorkOrder_ui and prx_master-ui (canonical source: prx_buyerapp-ui).
 * Change it in all four repos together.
 *
 * Cookies keep the exact legacy format the whole platform depends on:
 *   access-token  = JSON.stringify({ access_token, refresh_token })
 *   refresh-token = bare refresh-token string
 * localStorage carries the timing state (never sent to the server); its
 * `storage` events double as the cross-tab/cross-bundle signal on the shared
 * origin.
 */

export const ACCESS_TOKEN_COOKIE = "access-token";
export const REFRESH_TOKEN_COOKIE = "refresh-token";

export const AUTH_STORAGE_KEYS = {
  expiresAt: "prx.auth.expiresAt",
  idleTimeoutMs: "prx.auth.idleTimeoutMs",
  warnBeforeMs: "prx.auth.warnBeforeMs",
  lastActivityAt: "prx.auth.lastActivityAt",
  logoutAt: "prx.auth.logoutAt",
} as const;

export interface TokenPayload {
  access_token: string;
  refresh_token?: string | null;
}

/** Seconds, as returned by the gateway's login/refresh `session` field. */
export interface SessionConfig {
  idleTimeout?: number;
  warningBefore?: number;
}

const COOKIE_OPTIONS = { expires: 7, secure: true, sameSite: "Strict" as const };
const DEFAULT_ACCESS_TOKEN_SECONDS = 1800;

const safeGet = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};
const safeSet = (key: string, value: string): void => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* storage unavailable — timing degrades gracefully */
  }
};
const safeRemove = (key: string): void => {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
};

const minutesEnv = (name: string, fallbackMinutes: number): number => {
  const parsed = Number(process.env[name]);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallbackMinutes;
};

export function setTokens(
  token: TokenPayload,
  expiresInSeconds?: number,
  sessionConfig?: SessionConfig
): void {
  Cookies.set(ACCESS_TOKEN_COOKIE, JSON.stringify(token), COOKIE_OPTIONS);
  if (token.refresh_token) {
    Cookies.set(REFRESH_TOKEN_COOKIE, token.refresh_token, COOKIE_OPTIONS);
  }
  const seconds =
    Number.isFinite(expiresInSeconds) && (expiresInSeconds as number) > 0
      ? (expiresInSeconds as number)
      : DEFAULT_ACCESS_TOKEN_SECONDS;
  safeSet(AUTH_STORAGE_KEYS.expiresAt, String(Date.now() + seconds * 1000));
  if (sessionConfig?.idleTimeout && sessionConfig.idleTimeout > 0) {
    safeSet(AUTH_STORAGE_KEYS.idleTimeoutMs, String(sessionConfig.idleTimeout * 1000));
  }
  if (sessionConfig?.warningBefore && sessionConfig.warningBefore > 0) {
    safeSet(AUTH_STORAGE_KEYS.warnBeforeMs, String(sessionConfig.warningBefore * 1000));
  }
}

/** Raw cookie string — the exact value the gateway expects as the Authorization header. */
export function getAccessTokenRaw(): string | undefined {
  return Cookies.get(ACCESS_TOKEN_COOKIE);
}

export function getAccessToken(): TokenPayload | null {
  const raw = getAccessTokenRaw();
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function getRefreshToken(): string | undefined {
  return Cookies.get(REFRESH_TOKEN_COOKIE) || getAccessToken()?.refresh_token || undefined;
}

export function isAuthenticated(): boolean {
  return !!getAccessTokenRaw();
}

/** ms-epoch the access token expires at, or null when unknown (pre-deploy session). */
export function getExpiresAt(): number | null {
  const raw = safeGet(AUTH_STORAGE_KEYS.expiresAt);
  const parsed = Number(raw);
  return raw && Number.isFinite(parsed) ? parsed : null;
}

/**
 * Reads storage fresh on every call so a refresh performed by another tab is
 * picked up. Unknown expiry (no stored value) counts as expired — the
 * bootstrap refresh then normalizes it.
 */
export function isAccessTokenExpired(skewMs = 30_000): boolean {
  const expiresAt = getExpiresAt();
  if (expiresAt == null) return true;
  return Date.now() > expiresAt - skewMs;
}

export function getIdleTimeoutMs(): number {
  const stored = Number(safeGet(AUTH_STORAGE_KEYS.idleTimeoutMs));
  if (Number.isFinite(stored) && stored > 0) return stored;
  return minutesEnv("REACT_APP_SESSION_IDLE_TIMEOUT_MINUTES", 30) * 60_000;
}

export function getWarnBeforeMs(): number {
  const stored = Number(safeGet(AUTH_STORAGE_KEYS.warnBeforeMs));
  if (Number.isFinite(stored) && stored > 0) return stored;
  return minutesEnv("REACT_APP_SESSION_WARNING_MINUTES", 2) * 60_000;
}

export function getLastActivityAt(): number | null {
  const raw = safeGet(AUTH_STORAGE_KEYS.lastActivityAt);
  const parsed = Number(raw);
  return raw && Number.isFinite(parsed) ? parsed : null;
}

/** Shared across tabs/bundles: typing in /p2p keeps a /wom tab's session alive. */
export function setLastActivityAt(timestamp: number): void {
  safeSet(AUTH_STORAGE_KEYS.lastActivityAt, String(timestamp));
}

export function clearTokens(): void {
  Cookies.remove(ACCESS_TOKEN_COOKIE);
  Cookies.remove(REFRESH_TOKEN_COOKIE);
  safeRemove(AUTH_STORAGE_KEYS.expiresAt);
  safeRemove(AUTH_STORAGE_KEYS.idleTimeoutMs);
  safeRemove(AUTH_STORAGE_KEYS.warnBeforeMs);
  safeRemove(AUTH_STORAGE_KEYS.lastActivityAt);
  // logoutAt intentionally survives — it is the cross-tab logout broadcast.
}
